import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { format, parse, isValid } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { parseSchoolAccountCsv } from '../utils/parseSchoolAccountCsv';
import { parseToIso } from '../utils/dateDdMmYyyy';
import './Dashboard.css';

const TABLE_CELL = { border: '1px solid #ccc', padding: 6, textAlign: 'left' };

const DATE_DISPLAY = 'dd/MM/yyyy';

function displayStringToDate(str) {
  const t = String(str ?? '').trim();
  if (!t) return null;
  const d = parse(t, DATE_DISPLAY, new Date());
  return isValid(d) ? d : null;
}

function newClassBlock() {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    value: '',
    grade: '',
    studentCount: '',
    confirmed: false,
    students: [],
  };
}

function SchoolAccount() {
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolResults, setSchoolResults] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [classes, setClasses] = useState(() => [newClassBlock()]);
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [csvResultText, setCsvResultText] = useState('');
  const [csvPayload, setCsvPayload] = useState(null);
  const [csvFileKey, setCsvFileKey] = useState(0);

  const [teacherSchoolQuery, setTeacherSchoolQuery] = useState('');
  const [teacherSchoolResults, setTeacherSchoolResults] = useState([]);
  const [teacherSchoolsLoading, setTeacherSchoolsLoading] = useState(false);
  const [teacherSchoolsError, setTeacherSchoolsError] = useState('');
  const [teacherSelectedSchool, setTeacherSelectedSchool] = useState(null);
  const [teacherCount, setTeacherCount] = useState('');
  const [teacherValidTill, setTeacherValidTill] = useState('');
  const [teacherSubmitLoading, setTeacherSubmitLoading] = useState(false);
  const [teacherMessage, setTeacherMessage] = useState({ type: '', text: '' });

  const debounceRef = useRef(null);
  const teacherDebounceRef = useRef(null);
  const csvFileRef = useRef(null);

  const fetchSchools = useCallback(async (q) => {
    const trimmed = String(q).trim();
    if (!trimmed) {
      setSchoolResults([]);
      setSchoolsError('');
      return;
    }
    setSchoolsLoading(true);
    setSchoolsError('');
    try {
      const res = await api.get('/data/schools', { params: { q: trimmed } });
      setSchoolResults(res.data.schools || []);
    } catch (err) {
      setSchoolResults([]);
      setSchoolsError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to load schools'
      );
    } finally {
      setSchoolsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = schoolQuery.trim();
    if (!trimmed) {
      setSchoolResults([]);
      setSchoolsError('');
      return () => {};
    }
    debounceRef.current = setTimeout(() => {
      fetchSchools(trimmed);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [schoolQuery, fetchSchools]);

  const fetchTeacherSchools = useCallback(async (q) => {
    const trimmed = String(q).trim();
    if (!trimmed) {
      setTeacherSchoolResults([]);
      setTeacherSchoolsError('');
      return;
    }
    setTeacherSchoolsLoading(true);
    setTeacherSchoolsError('');
    try {
      const res = await api.get('/data/schools', { params: { q: trimmed } });
      setTeacherSchoolResults(res.data.schools || []);
    } catch (err) {
      setTeacherSchoolResults([]);
      setTeacherSchoolsError(
        err.response?.data?.error || err.response?.data?.details || 'Failed to load schools'
      );
    } finally {
      setTeacherSchoolsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (teacherDebounceRef.current) clearTimeout(teacherDebounceRef.current);
    const trimmed = teacherSchoolQuery.trim();
    if (!trimmed) {
      setTeacherSchoolResults([]);
      setTeacherSchoolsError('');
      return () => {};
    }
    teacherDebounceRef.current = setTimeout(() => {
      fetchTeacherSchools(trimmed);
    }, 350);
    return () => {
      if (teacherDebounceRef.current) clearTimeout(teacherDebounceRef.current);
    };
  }, [teacherSchoolQuery, fetchTeacherSchools]);

  const pickSchool = (s) => {
    setSelectedSchool(s);
    setSchoolQuery('');
    setSchoolResults([]);
  };

  const pickTeacherSchool = (s) => {
    setTeacherSelectedSchool(s);
    setTeacherSchoolQuery('');
    setTeacherSchoolResults([]);
  };

  const handleCreateTeacherAccounts = async () => {
    if (!teacherSelectedSchool || teacherSubmitLoading) return;
    const n = parseInt(teacherCount, 10);
    if (Number.isNaN(n) || n < 1) {
      setTeacherMessage({ type: 'error', text: 'Number of teachers must be a positive integer.' });
      return;
    }
    const vtIso = parseToIso(teacherValidTill);
    if (!vtIso) {
      setTeacherMessage({
        type: 'error',
        text: 'Valid till must be a valid date (dd/mm/yyyy or yyyy-mm-dd).',
      });
      return;
    }
    setTeacherSubmitLoading(true);
    setTeacherMessage({ type: '', text: '' });
    try {
      const res = await api.post('/data/teacher-accounts', {
        schoolId: teacherSelectedSchool.school_id,
        teacherCount: n,
        validTill: vtIso,
      });
      const tok = res.data.tokens || [];
      const schoolChinese =
        String(teacherSelectedSchool.chinese_name ?? '').trim() ||
        String(teacherSelectedSchool.english_name ?? '').trim() ||
        String(teacherSelectedSchool.school_id ?? '');
      const tokenList = tok.join(', ');
      setTeacherMessage({
        type: 'success',
        text: `學校︰${schoolChinese}\n認證碼︰${tokenList}`,
      });
    } catch (err) {
      const d = err.response?.data?.details;
      const e = err.response?.data?.error;
      setTeacherMessage({
        type: 'error',
        text: [d, e].filter(Boolean).join(' — ') || 'Failed to create teacher accounts',
      });
    } finally {
      setTeacherSubmitLoading(false);
    }
  };

  const updateClass = (index, field, raw) => {
    setClasses((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: raw };
      if (field === 'value' || field === 'grade' || field === 'studentCount') {
        row.confirmed = false;
        row.students = [];
      }
      next[index] = row;
      return next;
    });
  };

  const confirmClass = (index) => {
    const c = classes[index];
    if (!c) return;
    const n = parseInt(c.studentCount, 10);
    if (Number.isNaN(n) || n < 1) {
      setMessage({ type: 'error', text: 'Number of students must be a positive integer.' });
      return;
    }
    if (!String(c.value).trim()) {
      setMessage({ type: 'error', text: 'Class value is required before confirming.' });
      return;
    }
    if (String(c.grade).trim() === '' || Number.isNaN(parseInt(c.grade, 10))) {
      setMessage({ type: 'error', text: 'Class grade must be an integer.' });
      return;
    }
    setMessage({ type: '', text: '' });
    setClasses((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur) return prev;
      next[index] = {
        ...cur,
        confirmed: true,
        students: Array.from({ length: n }, () => ({
          surname: '',
          firstname: '',
          chinese_name: '',
          birthday: '',
        })),
      };
      return next;
    });
  };

  const updateStudent = (classIndex, studentIndex, field, value) => {
    setClasses((prev) => {
      const next = [...prev];
      const block = { ...next[classIndex] };
      const students = [...block.students];
      students[studentIndex] = { ...students[studentIndex], [field]: value };
      block.students = students;
      next[classIndex] = block;
      return next;
    });
  };

  const addClass = () => {
    setClasses((prev) => [...prev, newClassBlock()]);
  };

  const allClassesReady =
    classes.length > 0 &&
    classes.every((c) => c.confirmed && Array.isArray(c.students) && c.students.length > 0);

  const canSubmit =
    selectedSchool &&
    allClassesReady &&
    String(periodStartDate).trim() !== '' &&
    String(periodEndDate).trim() !== '' &&
    !submitLoading;

  const submitSchoolAccount = async (payload, onSuccess) => {
    setSubmitLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await api.post('/data/school-account', payload);
      setMessage({
        type: 'success',
        text: `Created ${res.data.createdStudents} student(s); class IDs: ${(res.data.classIds || []).join(', ')}`,
      });
      if (onSuccess) onSuccess();
      else {
        setSelectedSchool(null);
        setClasses([newClassBlock()]);
        setPeriodStartDate('');
        setPeriodEndDate('');
      }
    } catch (err) {
      const d = err.response?.data?.details;
      const e = err.response?.data?.error;
      setMessage({
        type: 'error',
        text: [d, e].filter(Boolean).join(' — ') || 'Failed to create student account',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const pStart = parseToIso(periodStartDate);
    const pEnd = parseToIso(periodEndDate);
    if (!pStart || !pEnd) {
      setMessage({
        type: 'error',
        text: 'Period start and end must be valid dates (dd/mm/yyyy or yyyy-mm-dd).',
      });
      return;
    }
    if (pEnd < pStart) {
      setMessage({ type: 'error', text: 'Period end must be on or after period start.' });
      return;
    }
    for (let ci = 0; ci < classes.length; ci += 1) {
      const c = classes[ci];
      for (let si = 0; si < c.students.length; si += 1) {
        const b = String(c.students[si].birthday ?? '').trim();
        if (b && !parseToIso(b)) {
          setMessage({
            type: 'error',
            text: `Class ${ci + 1}, student ${si + 1}: invalid birthday (dd/mm/yyyy or yyyy-mm-dd).`,
          });
          return;
        }
      }
    }
    const payload = {
      schoolId: selectedSchool.school_id,
      periodStartDate: pStart,
      periodEndDate: pEnd,
      classes: classes.map((c) => ({
        value: String(c.value).trim(),
        grade: parseInt(c.grade, 10),
        students: c.students.map((s) => ({
          surname: s.surname,
          firstname: s.firstname,
          chineseName: s.chinese_name,
          birthday: String(s.birthday ?? '').trim() ? parseToIso(String(s.birthday).trim()) : '',
        })),
      })),
    };
    await submitSchoolAccount(payload, null);
  };

  const onCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    setCsvPayload(null);
    if (!file) {
      setCsvResultText('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseSchoolAccountCsv(text);
      setCsvResultText(parsed.resultText);
      if (parsed.success) {
        setCsvPayload(parsed.payload);
      } else {
        setCsvPayload(null);
      }
    };
    reader.onerror = () => {
      setCsvResultText('Failed to read file.');
      setCsvPayload(null);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleCsvCreate = async () => {
    if (!csvPayload || submitLoading) return;
    await submitSchoolAccount(csvPayload, () => {
      setCsvPayload(null);
      setCsvResultText('');
      setCsvFileKey((k) => k + 1);
      if (csvFileRef.current) csvFileRef.current.value = '';
    });
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="School Account" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 960, margin: '0 auto' }}>
          {message.text ? <div className={`message ${message.type}`}>{message.text}</div> : null}

          <div className="form-section">
            <h2>School Account</h2>
          </div>

          <div className="form-section insert-form" style={{ marginBottom: 24 }}>
            <h3>Create teacher account</h3>
            {teacherMessage.text ? (
              <div className={`message ${teacherMessage.type}`} style={{ whiteSpace: 'pre-wrap' }}>
                {teacherMessage.text}
              </div>
            ) : null}
            <div className="form-group">
              <label>Find school (Chinese or English name)</label>
              <input
                type="text"
                value={teacherSchoolQuery}
                onChange={(e) => setTeacherSchoolQuery(e.target.value)}
                placeholder="Type to search…"
                autoComplete="off"
              />
              {teacherSchoolsLoading ? <small>Searching…</small> : null}
              {teacherSchoolsError ? <div className="message error">{teacherSchoolsError}</div> : null}
              {teacherSelectedSchool ? (
                <div style={{ marginTop: 8 }}>
                  <strong>Selected:</strong>{' '}
                  {[
                    teacherSelectedSchool.english_name?.trim() || null,
                    teacherSelectedSchool.chinese_name?.trim() || null,
                    teacherSelectedSchool.school_id != null && teacherSelectedSchool.school_id !== ''
                      ? String(teacherSelectedSchool.school_id)
                      : null,
                  ]
                    .filter((v) => v != null && v !== '')
                    .join(' / ')}{' '}
                  <button
                    type="button"
                    className="delete-question-button"
                    onClick={() => setTeacherSelectedSchool(null)}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
              {!teacherSelectedSchool && teacherSchoolResults.length > 0 ? (
                <ul className="sidebar-submenu" style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                  {teacherSchoolResults.map((s) => (
                    <li key={s.school_id} className="sidebar-item" onClick={() => pickTeacherSchool(s)}>
                      {[s.english_name, s.chinese_name].filter(Boolean).join(' — ') || `School #${s.school_id}`}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="form-group">
              <label>
                Number of teachers <span className="required">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={teacherCount}
                onChange={(e) => setTeacherCount(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>
                Valid till <span className="required">*</span>
              </label>
              <DatePicker
                selected={displayStringToDate(teacherValidTill)}
                onChange={(date) =>
                  setTeacherValidTill(date ? format(date, DATE_DISPLAY) : '')
                }
                dateFormat={DATE_DISPLAY}
                placeholderText="dd/mm/yyyy"
                autoComplete="off"
                isClearable
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                dropdownMode="select"
              />
            </div>
            <button
              type="button"
              className="submit-button"
              disabled={
                !teacherSelectedSchool ||
                teacherSubmitLoading ||
                String(teacherCount).trim() === '' ||
                String(teacherValidTill).trim() === ''
              }
              onClick={handleCreateTeacherAccounts}
            >
              {teacherSubmitLoading ? 'Creating…' : 'Confirm'}
            </button>
          </div>

          <div className="form-section insert-form" style={{ marginBottom: 24 }}>
            <h3>Upload student account CSV</h3>
            <p className="form-hint" style={{ marginBottom: 12 }}>
              Header columns: school_id, value, grade, surname, firstname, chinese_name, birthday,
              period_start_date (or eriod_start_date), period_end_date. Dates:{' '}
              <strong>dd/mm/yyyy</strong> (or yyyy-mm-dd). One row per student; same school_id and
              subscription dates on every row; group by value + grade for classes.
            </p>
            <div className="form-group">
              <label htmlFor="school-account-csv">CSV file</label>
              <input
                id="school-account-csv"
                key={csvFileKey}
                ref={csvFileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onCsvFileChange}
              />
            </div>
            {csvResultText ? (
              <div className="form-group">
                <label>Uploading result</label>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    maxHeight: 220,
                    overflow: 'auto',
                    padding: 12,
                    background: '#f6f6f6',
                    border: '1px solid #ddd',
                    fontSize: 13,
                    margin: 0,
                  }}
                >
                  {csvResultText}
                </pre>
              </div>
            ) : null}
            <button
              type="button"
              className="submit-button"
              disabled={!csvPayload || submitLoading}
              onClick={handleCsvCreate}
            >
              {submitLoading ? 'Creating…' : 'Create account'}
            </button>
          </div>

          <div className="form-section">
            <form onSubmit={handleCreate} className="insert-form">
              <div className="form-group">
                <label>Find school (Chinese or English name)</label>
                <input
                  type="text"
                  value={schoolQuery}
                  onChange={(e) => setSchoolQuery(e.target.value)}
                  placeholder="Type to search…"
                  autoComplete="off"
                />
                {schoolsLoading ? <small>Searching…</small> : null}
                {schoolsError ? <div className="message error">{schoolsError}</div> : null}
                {selectedSchool ? (
                  <div style={{ marginTop: 8 }}>
                    <strong>Selected:</strong>{' '}
                    {[
                      selectedSchool.english_name?.trim() || null,
                      selectedSchool.chinese_name?.trim() || null,
                      selectedSchool.school_id != null && selectedSchool.school_id !== ''
                        ? String(selectedSchool.school_id)
                        : null,
                    ]
                      .filter((v) => v != null && v !== '')
                      .join(' / ')}{' '}
                    <button type="button" className="delete-question-button" onClick={() => setSelectedSchool(null)}>
                      Clear
                    </button>
                  </div>
                ) : null}
                {!selectedSchool && schoolResults.length > 0 ? (
                  <ul className="sidebar-submenu" style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                    {schoolResults.map((s) => (
                      <li key={s.school_id} className="sidebar-item" onClick={() => pickSchool(s)}>
                        {[s.english_name, s.chinese_name].filter(Boolean).join(' — ') || `School #${s.school_id}`}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              {classes.map((block, ci) => (
                <div key={block.key} className="form-section" style={{ border: '1px solid var(--border, #ddd)', padding: 16, marginBottom: 16 }}>
                  <h3>Class {ci + 1}</h3>
                  <div className="form-group">
                    <label>
                      Class value <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={block.value}
                      onChange={(e) => updateClass(ci, 'value', e.target.value)}
                      disabled={block.confirmed}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Grade <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      value={block.grade}
                      onChange={(e) => updateClass(ci, 'grade', e.target.value)}
                      disabled={block.confirmed}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Number of students <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={block.studentCount}
                      onChange={(e) => updateClass(ci, 'studentCount', e.target.value)}
                      disabled={block.confirmed}
                    />
                  </div>
                  {!block.confirmed ? (
                    <button type="button" className="submit-button" onClick={() => confirmClass(ci)}>
                      Confirm student count
                    </button>
                  ) : (
                    <div style={{ marginTop: 16 }}>
                      <h4>Student details</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table
                          style={{
                            width: '100%',
                            minWidth: 520,
                            borderCollapse: 'collapse',
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={TABLE_CELL}>Surname</th>
                              <th style={TABLE_CELL}>First name</th>
                              <th style={TABLE_CELL}>Chinese name</th>
                              <th style={TABLE_CELL}>Birthday</th>
                            </tr>
                          </thead>
                          <tbody>
                            {block.students.map((row, si) => (
                              <tr key={si}>
                                <td style={TABLE_CELL}>
                                  <input
                                    type="text"
                                    value={row.surname}
                                    onChange={(e) => updateStudent(ci, si, 'surname', e.target.value)}
                                  />
                                </td>
                                <td style={TABLE_CELL}>
                                  <input
                                    type="text"
                                    value={row.firstname}
                                    onChange={(e) => updateStudent(ci, si, 'firstname', e.target.value)}
                                  />
                                </td>
                                <td style={TABLE_CELL}>
                                  <input
                                    type="text"
                                    value={row.chinese_name}
                                    onChange={(e) => updateStudent(ci, si, 'chinese_name', e.target.value)}
                                  />
                                </td>
                                <td style={TABLE_CELL}>
                                  <DatePicker
                                    selected={displayStringToDate(row.birthday)}
                                    onChange={(date) =>
                                      updateStudent(
                                        ci,
                                        si,
                                        'birthday',
                                        date ? format(date, DATE_DISPLAY) : ''
                                      )
                                    }
                                    dateFormat={DATE_DISPLAY}
                                    placeholderText="dd/mm/yyyy"
                                    autoComplete="off"
                                    isClearable
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    dropdownMode="select"
                                    withPortal
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="form-group">
                <button type="button" className="submit-button" onClick={addClass}>
                  Add another class
                </button>
              </div>

              <div className="form-section" style={{ marginTop: 24 }}>
                <h3>Subscription details</h3>
                <div className="form-group">
                  <label>
                    Period start <span className="required">*</span>
                  </label>
                  <DatePicker
                    selected={displayStringToDate(periodStartDate)}
                    onChange={(date) =>
                      setPeriodStartDate(date ? format(date, DATE_DISPLAY) : '')
                    }
                    dateFormat={DATE_DISPLAY}
                    placeholderText="dd/mm/yyyy"
                    autoComplete="off"
                    isClearable
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    dropdownMode="select"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Period end <span className="required">*</span>
                  </label>
                  <DatePicker
                    selected={displayStringToDate(periodEndDate)}
                    onChange={(date) =>
                      setPeriodEndDate(date ? format(date, DATE_DISPLAY) : '')
                    }
                    dateFormat={DATE_DISPLAY}
                    placeholderText="dd/mm/yyyy"
                    autoComplete="off"
                    isClearable
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    dropdownMode="select"
                  />
                </div>
              </div>

              <button type="submit" className="submit-button" disabled={!canSubmit}>
                {submitLoading ? 'Creating…' : 'Create account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolAccount;
